// src/pages/DebtStudio/lib/buildIssueStats.js

import {
  computeIRR,
  computeAverageCouponByBondYears,
  priceFromYield,
  computePurchasePrice,
  computeWeightedAvgCoupon,
  computeWeightedAvgPrice,
  computeWAL,
} from "lib/bondMath";
import { computeArbYield } from "lib/arbYield";
import { dayCountFraction } from "lib/dayCount";
import dayjs from "lib/dayjs";

function couponAsDecimal(value) {
  let coupon = Number(value);
  if (!Number.isFinite(coupon)) return 0;
  if (coupon > 1.5) coupon /= 100;
  return coupon;
}

/** Calculate the settlement accrued-interest receipt independently of DS adjustments. */
export function computeIssueAccruedInterest({
  bondRows = [],
  datedDate,
  deliveryDate,
  dayCount = "30/360",
} = {}) {
  if (!datedDate || !deliveryDate) return 0;

  const fraction = dayCountFraction(datedDate, deliveryDate, dayCount);
  if (!Number.isFinite(fraction) || fraction <= 0) return 0;

  const accrued = bondRows.reduce((sum, row) => {
    const principal = Number(row?.principal) || 0;
    if (principal <= 0) return sum;
    return sum + principal * couponAsDecimal(row?.coupon) * fraction;
  }, 0);

  return Math.round((accrued + Number.EPSILON) * 100) / 100;
}

/* ==========================================================
 * COI Yield Metric Defaults
 * ========================================================== */

export function defaultYieldFlagsForType(type) {
  const t = String(type || "").trim().toLowerCase();

  if (
    t === "underwriter's discount" ||
    t === "underwriter discount" ||
    t === "underwriter"
  ) {
    return {
      includeInTIC: true,
      includeInAIC: true,
      includeInArbYield: false,
    };
  }

  if (t === "credit enhancement") {
    return {
      includeInTIC: false,
      includeInAIC: true,
      includeInArbYield: true,
    };
  }

  if (t) {
    return {
      includeInTIC: false,
      includeInAIC: true,
      includeInArbYield: false,
    };
  }

  return {
    includeInTIC: false,
    includeInAIC: false,
    includeInArbYield: false,
  };
}

export function normalizeCOIRow(row = {}) {
  const defaults = defaultYieldFlagsForType(row.type);

  return {
    ...row,

    includeInTIC:
      typeof row.includeInTIC === "boolean"
        ? row.includeInTIC
        : defaults.includeInTIC,

    includeInAIC:
      typeof row.includeInAIC === "boolean"
        ? row.includeInAIC
        : defaults.includeInAIC,

    includeInArbYield:
      typeof row.includeInArbYield === "boolean"
        ? row.includeInArbYield
        : defaults.includeInArbYield,
  };
}

export function collapseTermGroups(rows = []) {
  if (!Array.isArray(rows)) return [];

  const out = [];
  const groups = new Map();

  for (const r of rows) {
    const isTerm = String(r?.bond_type || "").toLowerCase() === "term";

    if (isTerm) {
      const key = r.term_group || r.date;

      if (!groups.has(key)) {
        groups.set(key, {
          date: key,
          principal: 0,
          coupon: null,
          price: null,
          bond_type: "term",
          call_date: "",
          latestDate: null,
          latestRow: null,
        });
      }

      const grp = groups.get(key);
      const p = Number(r.principal);

      if (Number.isFinite(p)) grp.principal += p;

      if (!grp.latestDate || new Date(r.date) > new Date(grp.latestDate)) {
        grp.latestDate = r.date;
        grp.latestRow = r;
      }
    } else {
      out.push(r);
    }
  }

  for (const grp of groups.values()) {
    const final = grp.latestRow;

    if (final) {
      grp.date = final.date;
      grp.call_date = final.call_date || "";
      grp.coupon = final.coupon;
      grp.price = final.price;
    }

    out.push(grp);
  }

  out.sort((a, b) => new Date(a.date) - new Date(b.date));

  return out;
}

export function bondYearBuckets(schedule = [], startBasisDate, firstPrincipalDate) {
  if (!schedule?.length || !startBasisDate || !firstPrincipalDate) return [];

  const startDate = dayjs(startBasisDate);
  const anchorEnd = dayjs(firstPrincipalDate);
  const lastDate = dayjs(schedule[schedule.length - 1]?.date);

  if (!startDate.isValid() || !anchorEnd.isValid() || !lastDate.isValid()) {
    return [];
  }

  const month = anchorEnd.month();
  const day = anchorEnd.date();

  let firstEnd = startDate.month(month).date(day);

  if (!firstEnd.isAfter(startDate, "day")) {
    firstEnd = firstEnd.add(1, "year");
  }

  const periods = [];
  let start = startDate.clone();
  let end = firstEnd.clone();

  while (end.isBefore(lastDate, "day") || end.isSame(lastDate, "day")) {
    periods.push({ start: start.clone(), end: end.clone() });
    start = end.clone();
    end = end.add(1, "year");
  }

  return periods.map((p) => {
    const total = schedule.reduce((sum, r) => {
      const d = dayjs(r.date);

      return d.isAfter(p.start, "day") && d.isSameOrBefore(p.end, "day")
        ? sum + (Number(r.total) || 0)
        : sum;
    }, 0);

    return {
      start: p.start.toISOString(),
      end: p.end.toISOString(),
      total,
    };
  });
}

export function computeCOIRowAmount(row, parAmount) {
  const amount = Number(row?.amount) || 0;
  const rateType = row?.rateType || "fixed";
  const par = Number(parAmount) || 0;

  if (rateType === "per1000") {
    return (amount / 1000) * par;
  }

  if (rateType === "percent") {
    return (amount / 100) * par;
  }

  return amount;
}

export function computeIncludedCOI(costRows = [], parAmount, includeKey) {
  if (!Array.isArray(costRows)) return 0;

  return costRows.reduce((sum, row) => {
    if (row?.[includeKey] !== true) return sum;
    return sum + computeCOIRowAmount(row, parAmount);
  }, 0);
}

export function isUnderwriterDiscountType(type) {
  const normalized = String(type || "").trim().toLowerCase();

  return (
    normalized === "underwriter's discount" ||
    normalized === "underwriter discount" ||
    normalized === "underwriter"
  );
}

export function computeUnderwriterDiscount(costRows = [], parAmount) {
  if (!Array.isArray(costRows)) return 0;

  return costRows.reduce((sum, row) => {
    if (!isUnderwriterDiscountType(row?.type)) return sum;
    return sum + computeCOIRowAmount(row, parAmount);
  }, 0);
}

export function computeIssueDuration({
  cashFlows = [],
  yieldRate,
  compPerYear = 2,
}) {
  const y = Number(yieldRate);
  const m = Number(compPerYear) || 2;

  if (!Array.isArray(cashFlows) || !cashFlows.length || !Number.isFinite(y)) {
    return { macaulayDuration: null, modifiedDuration: null };
  }

  let pvTotal = 0;
  let weightedPvTotal = 0;

  for (const cf of cashFlows) {
    const t = Number(cf.t) || 0;
    const amount = Math.abs(Number(cf.amount) || 0);

    if (t <= 0 || amount <= 0) continue;

    const discountFactor = Math.pow(1 + y / m, m * t);
    const pv = amount / discountFactor;

    pvTotal += pv;
    weightedPvTotal += t * pv;
  }

  if (pvTotal <= 0) {
    return { macaulayDuration: null, modifiedDuration: null };
  }

  const macaulayDuration = weightedPvTotal / pvTotal;
  const modifiedDuration = macaulayDuration / (1 + y / m);

  return { macaulayDuration, modifiedDuration };
}

export function computeAverageAnnualDebtService({
  totalDebtService,
  accruedInterest = 0,
  startDate,
  endDate,
  dayCount = "30/360",
} = {}) {
  if (!startDate || !endDate) return 0;

  const years = dayCountFraction(startDate, endDate, dayCount);
  if (!Number.isFinite(years) || years <= 0) return 0;

  const adjustedDebtService = Math.max(
    0,
    (Number(totalDebtService) || 0) - (Number(accruedInterest) || 0)
  );

  return adjustedDebtService / years;
}

function median(values = []) {
  const sorted = values
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

function periodsPerYear(frequency) {
  const normalized = String(frequency || "").trim().toLowerCase();
  const values = {
    monthly: 12,
    quarterly: 4,
    semiannual: 2,
    "semi-annual": 2,
    annual: 1,
  };
  return values[normalized] || 1;
}

/**
 * Separate a loan's recurring payment burden from incremental balloon principal.
 * The final scheduled principal/payment remains in recurring debt service; only the
 * excess caused by shortening the loan term below the amortization term is removed.
 */
export function computeLoanDebtServiceMetrics({
  schedule = [],
  form = {},
} = {}) {
  const rows = (schedule || []).filter((row) => row?.date);
  const amortizationPeriods = Number(
    form?.amortizationPeriods ?? form?.amortization_periods
  );
  const loanTermPeriods = Number(
    form?.loanTermPeriods ?? form?.loan_term_periods
  );
  const hasBalloon =
    rows.length > 1 &&
    Number.isFinite(amortizationPeriods) &&
    Number.isFinite(loanTermPeriods) &&
    amortizationPeriods > loanTermPeriods;

  if (!rows.length) {
    return {
      hasBalloon: false,
      balloonPrincipal: 0,
      incrementalBalloonPrincipal: 0,
      recurringTotalDebtService: 0,
      recurringAvgAnnualDS: 0,
    };
  }

  const finalRow = rows[rows.length - 1];
  const finalPrincipal = Number(finalRow?.principal) || 0;
  const finalInterest = Number(finalRow?.interest) || 0;
  let incrementalBalloonPrincipal = 0;

  if (hasBalloon) {
    const priorRows = rows.slice(0, -1);
    const structureMode = String(
      form?.structureMode ?? form?.structure_mode ?? "level_payment"
    ).toLowerCase();

    if (structureMode === "level_principal") {
      const scheduledPrincipal = median(
        priorRows.map((row) => Number(row?.principal) || 0).filter((v) => v > 0)
      );
      incrementalBalloonPrincipal = Math.max(
        0,
        finalPrincipal - scheduledPrincipal
      );
    } else {
      const recurringPayment = median(
        priorRows.map((row) => Number(row?.total) || 0).filter((v) => v > 0)
      );
      const scheduledFinalPrincipal = Math.max(
        0,
        recurringPayment - finalInterest
      );
      incrementalBalloonPrincipal = Math.max(
        0,
        finalPrincipal - scheduledFinalPrincipal
      );
    }
  }

  const recurringSchedule = rows.map((row, index) => {
    if (index !== rows.length - 1 || incrementalBalloonPrincipal <= 0) {
      return row;
    }
    return {
      ...row,
      principal: Math.max(
        0,
        (Number(row?.principal) || 0) - incrementalBalloonPrincipal
      ),
      total: Math.max(
        0,
        (Number(row?.total) || 0) - incrementalBalloonPrincipal
      ),
    };
  });
  const recurringTotalDebtService = recurringSchedule.reduce(
    (sum, row) => sum + (Number(row?.total) || 0),
    0
  );
  const paymentFrequency =
    form?.couponFrequency ??
    form?.coupon_frequency ??
    form?.principalFrequency ??
    form?.principal_frequency;
  const annualPaymentCount = periodsPerYear(paymentFrequency);
  const recurringYears = rows.length / annualPaymentCount;
  const recurringAvgAnnualDS =
    recurringYears > 0
      ? recurringTotalDebtService / recurringYears
      : recurringTotalDebtService;
  return {
    hasBalloon: incrementalBalloonPrincipal > 0,
    balloonPrincipal: incrementalBalloonPrincipal > 0 ? finalPrincipal : 0,
    incrementalBalloonPrincipal,
    recurringTotalDebtService,
    recurringAvgAnnualDS,
  };
}

/** DBC-style maturity pricing PV01 using a one-basis-point yield increase. */
export function computePricingPV01({
  bondRows = [],
  deliveryDate,
  dayCount = "30/360",
  couponFrequency = "semiannual",
} = {}) {
  if (!Array.isArray(bondRows) || !bondRows.length || !deliveryDate) return null;

  let pv01 = 0;
  let pricedRows = 0;

  for (const row of bondRows) {
    const principal = Number(row?.principal) || 0;
    const couponPct = Number(row?.coupon);
    const yieldPct = Number(row?.yield);
    const maturityDate = row?.date || row?.maturity || row?.maturity_date;

    if (
      principal <= 0 ||
      !Number.isFinite(couponPct) ||
      !Number.isFinite(yieldPct) ||
      !maturityDate
    ) {
      continue;
    }

    const maturity = dayjs(maturityDate);
    const callDateValue = row?.call_date || row?.callDate;
    const callDate = callDateValue ? dayjs(callDateValue) : null;
    const redemptionDate =
      callDate?.isValid() &&
      callDate.isAfter(dayjs(deliveryDate), "day") &&
      callDate.isBefore(maturity, "day")
        ? callDate.format("YYYY-MM-DD")
        : maturityDate;

    const pricingInputs = {
      couponRate: couponPct / 100,
      deliveryDate,
      maturityDate: redemptionDate,
      dayCount,
      frequency: couponFrequency,
      applyParSnap: false,
    };
    const basePrice = priceFromYield({
      ...pricingInputs,
      yieldRate: yieldPct / 100,
    });
    const bumpedPrice = priceFromYield({
      ...pricingInputs,
      yieldRate: (yieldPct + 0.01) / 100,
    });

    if (!Number.isFinite(basePrice) || !Number.isFinite(bumpedPrice)) continue;

    pv01 += ((basePrice - bumpedPrice) * principal) / 100;
    pricedRows += 1;
  }

  return pricedRows > 0 ? pv01 : null;
}

export function buildIssueStats({ form = {}, bondRows = [], flowRows = [] } = {}) {
  if (!bondRows.length || !flowRows.length) return null;

  const {
    datedDate,
    deliveryDate,
    dayCount,
    costOfIssuance,
    cost_of_issuance,
    firstPrincipalDate,
    first_principal_date,
    taxStatus,
    tax_status,
    financingType,
    financing_type,
    couponFrequency,
    coupon_frequency,
  } = form || {};

  const statsDate = deliveryDate || datedDate;

  if (!statsDate) return null;

  const collapsed = collapseTermGroups(bondRows);
  const schedule = flowRows || [];

  if (!schedule.length) return null;

  const dc = dayCount || "30/360";

  const firstPayment = schedule[0]?.date || "";
  const lastPayment = schedule[schedule.length - 1]?.date || "";

  const effectiveFirstPrincipalDate = firstPrincipalDate || first_principal_date;
  const effectiveTaxStatus = taxStatus || tax_status;

  const totalPrincipal = schedule.reduce(
    (s, r) => s + (Number(r.principal) || 0),
    0
  );

  const totalInterest = schedule.reduce(
    (s, r) => s + (Number(r.interest) || 0),
    0
  );

  const totalDebtService = totalPrincipal + totalInterest;

  const principalFlows = schedule.map((r) => ({
    date: r.date,
    principal: Number(r.principal) || 0,
  }));

  const wal = computeWAL(principalFlows, statsDate, dc);

  const byds = bondYearBuckets(
    schedule,
    statsDate,
    effectiveFirstPrincipalDate
  );

  const maxAnnualDS = byds.length
    ? Math.max(...byds.map((b) => Number(b.total) || 0))
    : 0;

  const purchasePrice = computePurchasePrice(collapsed);

  const grossCfBase = schedule.map((r) => ({
    t: dayCountFraction(statsDate, r.date, dc),
    amount: -(Number(r.total) || 0),
  }));

  const accruedInterest = computeIssueAccruedInterest({
    bondRows: collapsed,
    datedDate,
    deliveryDate,
    dayCount: dc,
  });
  const avgAnnualDS = computeAverageAnnualDebtService({
    totalDebtService,
    accruedInterest,
    startDate: statsDate,
    endDate: lastPayment,
    dayCount: dc,
  });

  const finalPar = collapsed.reduce(
    (s, r) => s + (Number(r.principal) || 0),
    0
  );

  const coiRows = (
    costOfIssuance ||
    cost_of_issuance ||
    []
  ).map(normalizeCOIRow);

  const ticCosts = computeIncludedCOI(coiRows, finalPar, "includeInTIC");
  const aicCosts = computeIncludedCOI(coiRows, finalPar, "includeInAIC");
  const arbYieldCosts = computeIncludedCOI(
    coiRows,
    finalPar,
    "includeInArbYield"
  );

  const underwriterDiscount = computeUnderwriterDiscount(
    coiRows,
    finalPar
  );

  const compPerYear = 2;

  const ticTargetValue =
    purchasePrice + accruedInterest - ticCosts;

  const aicTargetValue =
    purchasePrice + accruedInterest - aicCosts;

  const tic = computeIRR(
    [{ t: 0, amount: ticTargetValue }, ...grossCfBase],
    compPerYear
  );

  const arbYield =
    effectiveTaxStatus === "tax_exempt"
      ? computeArbYield({
          bondRows: collapsed,
          flowRows: schedule,
          issueDate: statsDate,
          dayCountBasis: "30/360",
          issuePrice: purchasePrice + accruedInterest,
          issuePriceAdjustment: arbYieldCosts,
        })
      : null;

  const aic = computeIRR(
    [{ t: 0, amount: aicTargetValue }, ...grossCfBase],
    compPerYear
  );

  const netPremiumDiscount = purchasePrice - finalPar;

  const netInterestCost =
    totalInterest + underwriterDiscount - netPremiumDiscount;

  const nicWal = computeWAL(
    principalFlows,
    datedDate || statsDate,
    dc
  );

  const nic =
    finalPar > 0 && nicWal > 0
      ? netInterestCost / (finalPar * nicWal)
      : null;

  const isLoan =
    String(financingType || financing_type || "").trim().toLowerCase() ===
    "loan";
  const loanDebtServiceMetrics = isLoan
    ? computeLoanDebtServiceMetrics({
        schedule,
        form,
        startDate: statsDate,
        dayCount: dc,
      })
    : null;
  const wac = isLoan
    ? computeWeightedAvgCoupon(collapsed)
    : computeAverageCouponByBondYears(schedule, datedDate, dc);
  const wap = computeWeightedAvgPrice(collapsed);

  // DBC-style issue duration discounts gross contractual cash flows at TIC.
  const { macaulayDuration, modifiedDuration } =
    computeIssueDuration({
      cashFlows: grossCfBase,
      yieldRate: tic,
      compPerYear,
    });
  const pv01 = isLoan
    ? null
    : computePricingPV01({
        bondRows,
        deliveryDate: deliveryDate || datedDate,
        dayCount: dc,
        couponFrequency: couponFrequency || coupon_frequency || "semiannual",
      });

  return {
    totalPrincipal,
    totalInterest,
    totalDebtService,
    firstPayment,
    lastPayment,
    wal,
    macaulayDuration,
    modifiedDuration,
    avgAnnualDS: isLoan
      ? loanDebtServiceMetrics.recurringAvgAnnualDS
      : avgAnnualDS,
    maxAnnualDS: isLoan ? null : maxAnnualDS,
    recurringTotalDebtService: loanDebtServiceMetrics?.recurringTotalDebtService,
    hasBalloon: loanDebtServiceMetrics?.hasBalloon || false,
    balloonPrincipal: loanDebtServiceMetrics?.balloonPrincipal || 0,
    incrementalBalloonPrincipal:
      loanDebtServiceMetrics?.incrementalBalloonPrincipal || 0,
    tic,
    nic,
    aic,
    wac,
    wap,
    arbYield,
    pv01,
    purchasePrice,
    accruedInterest,
    finalPar,
    ticCosts,
    aicCosts,
    arbYieldCosts,
    underwriterDiscount,
  };
}
